import { Injectable, OnDestroy } from '@angular/core';
import { RechtRubrik } from './recht-rubrik';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { RubrikConfigEntry } from '../../../../core/base-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AppCustomConfigService } from '../../../../core/services/app-custom-config.service';
import { StandardrechtMasterDetailEntryMeta } from '../../../../core/gb-api';
import { AbstractComponent } from '../../../../shared/components/abstract-component/abstract.component';
import RubrikEnum = RubrikConfigEntry.RubrikEnum;
import RechtArtEnum = StandardrechtMasterDetailEntryMeta.RechtArtEnum;

@Injectable()
export class RechtRubrikService extends AbstractComponent implements OnDestroy {
  private rubrikComponents: Array<RechtRubrik> = [];

  public constructor(
    private configService: AppCustomConfigService,
    private appConfigService: AppConfigService
  ) {
    super();
    this.appConfigService.kantonChange$.subscribe(() => this.buildRubrikComponents());
    this.buildRubrikComponents();
    keysToTranslate<string>([
      'BelastetOderBerechtigt',
      'DienstbarkeitDetail',
      'Perimeter',
      'Gläubiger',
      'PfandrechtDetail',
      'Pfandtitel',
      'Schuldner',
      'HaengigeGeschaefteAufträge'
    ]);
  }

  private getRubrikItem(element: string): RechtRubrik {
    return displayRubriken.filter((m) => m.element.toString() === element).pop();
  }

  public getRubriken(rechtArt: RechtArtEnum): Array<RechtRubrik> {
    if (rechtArt === 'PFANDRECHT') {
      return this.rubrikComponents.filter((r) => r.parent === 'GB_DETAIL_PFANDRECHT');
    }
    return this.rubrikComponents.filter((r) => r.parent === 'GB_DETAIL_STANDARDRECHT');
  }

  private buildRubrikComponents() {
    this.rubrikComponents = [];
    const sub = this.configService.getRubrikConfigs().subscribe((rubrikConfigEntries) => {
      this.createRubrikComponentsFor(rubrikConfigEntries, 'GB_DETAIL_STANDARDRECHT');
      this.createRubrikComponentsFor(rubrikConfigEntries, 'GB_DETAIL_PFANDRECHT');
    });
    this.subscriptions.push(sub);
  }

  private createRubrikComponentsFor(rubrikComponentConfigEntries, rubrik: RubrikEnum) {
    const current = this.filter(rubrikComponentConfigEntries, rubrik);
    if (!current || !current.components || current.components.length === 0) {
      // no configuration -> show default behaviour / all components
      displayRubriken
        .filter((r) => r.parent === rubrik)
        .forEach((displayItem) => this.rubrikComponents.push(<RechtRubrik>{ ...displayItem }));
      return;
    }
    // show only configured components
    this.lookUp(current.components, this).forEach((c) => this.rubrikComponents.push(c));
  }

  private filter(rubrikConfigEntries: Array<RubrikConfigEntry>, selected: RubrikEnum): RubrikConfigEntry {
    const selectedItem = rubrikConfigEntries.filter((a) => a.rubrik === selected);
    if (selectedItem && selectedItem[0]) {
      return selectedItem[0];
    }
    return null;
  }

  private lookUp(configuredComponentList: Array<string>, that): Array<RechtRubrik> {
    const rubriken: Array<RechtRubrik> = [];
    configuredComponentList.forEach(function (component: string) {
      const displayItem = that.getRubrikItem(component);
      if (displayItem) {
        rubriken.push(<RechtRubrik>{ ...displayItem });
      }
    });
    return rubriken;
  }
}

const displayRubriken: Array<RechtRubrik> = [
  <RechtRubrik>{
    title: 'BelastetOderBerechtigt',
    id: 'belastet_berechtigt_standardrecht',
    isOpen: true,
    isClosable: true,
    element: 'detailStandardrechtBelastungen',
    parent: 'GB_DETAIL_STANDARDRECHT'
  },
  <RechtRubrik>{
    title: 'Perimeter',
    id: 'standardrecht_perimeter',
    isOpen: true,
    isClosable: true,
    element: 'detailStandardrechtPerimeter',
    parent: 'GB_DETAIL_STANDARDRECHT'
  },
  <RechtRubrik>{
    title: 'HaengigeGeschaefteAufträge',
    id: 'haengige_geschaefte_standardrecht',
    isOpen: true,
    isClosable: true,
    element: 'detailStandardrechtHaengigeGeschaefte',
    parent: 'GB_DETAIL_STANDARDRECHT'
  },
  <RechtRubrik>{
    title: 'BelastetOderBerechtigt',
    id: 'pfandrecht_detail_pfandrechtbelastungen',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtBelastungen',
    parent: 'GB_DETAIL_PFANDRECHT'
  },
  <RechtRubrik>{
    title: 'Gläubiger',
    id: 'glaeubiger',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtGlaeubiger',
    parent: 'GB_DETAIL_PFANDRECHT'
  },
  <RechtRubrik>{
    title: 'Schuldner',
    id: 'schuldner',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtSchuldner',
    parent: 'GB_DETAIL_PFANDRECHT'
  },
  <RechtRubrik>{
    title: 'Pfandtitel',
    id: 'pfandtitel',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtPfandtitel',
    parent: 'GB_DETAIL_PFANDRECHT'
  },
  <RechtRubrik>{
    title: 'Perimeter',
    id: 'pfandrecht_perimeter',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtPerimeter',
    parent: 'GB_DETAIL_PFANDRECHT'
  },
  <RechtRubrik>{
    title: 'HaengigeGeschaefteAufträge',
    id: 'haengige_geschaefte_pfandrecht',
    isOpen: true,
    isClosable: true,
    element: 'detailPfandrechtHaengigeGeschaefte',
    parent: 'GB_DETAIL_PFANDRECHT'
  }
];
